
import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import CreateMerchantPage from './pages/CreateMerchantPage';
import MerchantProfilesPage from './pages/MerchantProfilesPage';
import { CreateMerchantProfilePage } from './pages/CreateMerchantProfilePage';
import MerchantsPage from './pages/MerchantsPage';
import PartnersPage from './pages/PartnersPage';
import CreatePartnerPage from './pages/CreatePartnerPage';
import EditPartnerPage from './pages/EditPartnerPage';
import PartnerDetailPage from './pages/PartnerDetailPage';
import { PayersPage } from './pages/PayersPage';
import { CreatePayerPage } from './pages/CreatePayerPage';
import PayerDetailPage from './pages/PayerDetailPage';
import PaymentMethodDetailPage from './pages/PaymentMethodDetailPage';
import SplitPayTransactionsPage from './pages/SplitPayTransactionsPage';
import CreateSplitPayTransactionPage from './pages/CreateSplitPayTransactionPage';
import SplitPayTransactionDetailPage from './pages/SplitPayTransactionDetailPage';

// Layout component
const Layout = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen bg-background">
    <header className="border-b">
      <div className="container mx-auto px-4 py-4">
        <h1 className="text-2xl font-bold">ProPay Simulator Admin</h1>
      </div>
    </header>
    <main className="container mx-auto px-4 py-8">
      {children}
    </main>
  </div>
);

// Home page component with navigation
const HomePage = () => (
  <Layout>
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-2">Welcome to ProPay Simulator Admin</h2>
        <p className="text-muted-foreground mb-6">
          Manage merchants and merchant profiles for ProPay integration testing.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <div className="p-6 border rounded-lg">
          <h3 className="font-semibold mb-2">Partners</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Manage partner entities
          </p>
          <a
            href="/partners"
            className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors border border-input bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2"
          >
            Partners
          </a>
        </div>

        <div className="p-6 border rounded-lg">
          <h3 className="font-semibold mb-2">Merchants</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Manage basic merchant entities
          </p>
          <a
            href="/merchants"
            className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors border border-input bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2"
          >
            Merchants
          </a>
        </div>

        <div className="p-6 border rounded-lg">
          <h3 className="font-semibold mb-2">Profiles</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Manage merchant profiles with processor data
          </p>
          <a
            href="/merchant-profiles"
            className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors border border-input bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2"
          >
            Profiles
          </a>
        </div>

        <div className="p-6 border rounded-lg">
          <h3 className="font-semibold mb-2">Payers</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Manage payer entities
          </p>
          <a
            href="/payers"
            className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors border border-input bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2"
          >
            Payers
          </a>
        </div>

        <div className="p-6 border rounded-lg">
          <h3 className="font-semibold mb-2">Transactions</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Manage ransactions
          </p>
          <a
            href="/splitpay-transactions"
            className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors border border-input bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2"
          >
            Transactions
          </a>
        </div>
      </div>
    </div>
  </Layout>
);

// Create router configuration
const router = createBrowserRouter([
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/merchants',
    element: <MerchantsPage />,
  },
  {
    path: '/merchants/create',
    element: <CreateMerchantPage />,
  },
  {
    path: '/merchant-profiles',
    element: <MerchantProfilesPage />,
  },
  {
    path: '/merchant-profiles/create',
    element: <CreateMerchantProfilePage />,
  },
  {
    path: '/payers',
    element: <PayersPage />,
  },
  {
    path: '/payers/create',
    element: <CreatePayerPage />,
  },
  {
    path: '/payers/:payerId',
    element: <PayerDetailPage />,
  },
  {
    path: '/payment-methods/:paymentMethodId',
    element: <PaymentMethodDetailPage />,
  },
  {
    path: '/splitpay-transactions',
    element: <SplitPayTransactionsPage />,
  },
  {
    path: '/splitpay-transactions/create',
    element: <CreateSplitPayTransactionPage />,
  },
  {
    path: '/splitpay-transactions/:transactionId',
    element: <SplitPayTransactionDetailPage />,
  },
  {
    path: '/partners',
    element: <PartnersPage />,
  },
  {
    path: '/partners/create',
    element: <CreatePartnerPage />,
  },
  {
    path: '/partners/:partnerId/edit',
    element: <EditPartnerPage />,
  },
  {
    path: '/partners/:partnerId',
    element: <PartnerDetailPage />,
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);

// Router component
const AppRouter = () => {
  return <RouterProvider router={router} />;
};

export default AppRouter;
